import { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLoaderData, useNavigate, useParams } from 'react-router';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { TrashIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { addCustomModel } from '../../api/registry';
import { registryActions } from '@/stores/registryStore';
import { useSpeciesData } from './hooks/useSpeciesData';
import Tooltip from '@/components/core/Tooltip';
import {
  SparklesIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  BookOpenIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import AIChatPanel from './AIChatPanel';
import { PERMISSION_OPTIONS } from '@/consts';
import { useIsFeatureEnabled } from '@/stores/settingsStore';
import { rowSlugs } from './equationModel/slugs';
import { collectValidationIssues } from './equationModel/validation';
import { buildCreatePayload } from './equationModel/payload';
import { useModuleValidation } from './equationModel/hooks/useModuleValidation';
import { useModuleTest } from './equationModel/hooks/useModuleTest';
import { useAiModuleBridge } from './equationModel/hooks/useAiModuleBridge';
import { useSpeciesBaseline } from './equationModel/hooks/useSpeciesBaseline';
import {
  EMISSION_SPECIES,
  PARAM_UNITS,
  PARAM_TYPES,
  DERIVED_ACCESSORS,
  DERIVED_ACCESSOR_UNITS,
  ENERGY_DERIVED_ACCESSORS,
} from './equationModel/constants';
import { EXTERNAL_IMPORT_PROMPT, blankDefaults, exampleDefaults } from './equationModel/defaults';
import { SectionHeader } from './equationModel/fields';
import HelpSection from './equationModel/HelpSection';
import TestResultsPanel from './equationModel/TestResultsPanel';
import TEASection from './equationModel/TEASection';
import BalanceEditor from './equationModel/BalanceEditor';
import SymbolSummary from './equationModel/SymbolSummary';
import { specToForm } from './equationModel/fromSpec';
import SpeciesChangeNotice from './equationModel/SpeciesChangeNotice';

// Equation-based custom module builder. Inputs / outputs are picked by species
// type, labels default to the species's display name (editable), and the slug
// (used in balance expressions as input.<slug>, param.<slug>) is auto-derived
// and shown read-only.
//
// `mode`: 'create' starts blank; 'duplicate' starts from the saved module the
// route loader fetched and saves it as a new module, keeping each row's saved
// name (see specToForm). Saved modules are never changed in place.

const GUIDE_URL = 'https://docs.sesame.software/user-guides/tutorials/equations.html';

const CreateEquationModelModal = ({ mode = 'create' }) => {
  const params = useParams();
  const { customModel } = useLoaderData() ?? {};
  const navigate = useNavigate();
  const isAgentEnabled = useIsFeatureEnabled('agent');
  const {
    typeOptions,
    typeToUnitsMap,
    typeToPropertiesMap,
    typeToEnergyUnitMap,
    isLoading: speciesLoading,
    error: speciesError,
    retry: retrySpecies,
  } = useSpeciesData();

  const canonicalUnitFor = useCallback(
    speciesName => typeToUnitsMap?.[speciesName]?.[0]?.label ?? '',
    [typeToUnitsMap],
  );

  const speciesByName = useMemo(() => {
    const map = {};
    typeOptions.forEach(t => {
      map[t.value] = t.label;
    });
    return map;
  }, [typeOptions]);

  const defaultSpecies = typeOptions[0]?.value ?? '';
  const defaultSpeciesLabel = typeOptions[0]?.label ?? '';

  // Labels seeded by the template's default values. We treat these as
  // auto-generated so that picking a species from the dropdown replaces
  // them with the species display label — matching what users expect
  // after their first dropdown interaction.
  const TEMPLATE_SEED_LABELS = useMemo(
    () => new Set(['Gas', 'Electricity', 'CO2', 'CH4 Leak']),
    [],
  );

  // Form starts blank; the "Load example" button reseeds it from
  // `exampleDefaults()`. Both are factories — see the note in defaults.js.
  const newBlankForm = useCallback(
    () => ({ ...blankDefaults(defaultSpecies), project_ids: [params.projectId] }),
    [defaultSpecies, params.projectId],
  );

  const { register, control, handleSubmit, setValue, getValues, reset } = useForm({
    defaultValues: customModel?.data ? specToForm(customModel, params.projectId) : newBlankForm(),
  });

  const inputsArray = useFieldArray({ control, name: 'inputs' });
  const outputsArray = useFieldArray({ control, name: 'outputs' });
  const paramsArray = useFieldArray({ control, name: 'params' });

  // Live-watched values for slug derivation and emissions auto-flag.
  const watchedInputs = useWatch({ control, name: 'inputs' });
  const watchedOutputs = useWatch({ control, name: 'outputs' });
  const watchedParams = useWatch({ control, name: 'params' });
  const watchedTea = useWatch({ control, name: 'tea' });
  const watchedLabel = useWatch({ control, name: 'label' });
  const watchedPermissions = useWatch({ control, name: 'permissions' });

  const inputSlugs = useMemo(
    () => rowSlugs(watchedInputs),
    [watchedInputs],
  );
  const outputSlugs = useMemo(
    () => rowSlugs(watchedOutputs),
    [watchedOutputs],
  );
  // Params get their own slug namespace.
  const paramSlugs = useMemo(
    () => rowSlugs(watchedParams),
    [watchedParams],
  );

  // Unit arrays in parallel with the slug arrays.
  const inputUnits = useMemo(
    () => (watchedInputs ?? []).map(io => canonicalUnitFor(io.species)),
    [watchedInputs, canonicalUnitFor],
  );
  const outputUnits = useMemo(
    () => (watchedOutputs ?? []).map(io => canonicalUnitFor(io.species)),
    [watchedOutputs, canonicalUnitFor],
  );
  const paramUnits = useMemo(
    () => (watchedParams ?? []).map(p => p.unit || 'scalar'),
    [watchedParams],
  );

  // Per-IO species property map: slug -> { property_alias -> unit_string }.
  // Combined inputs + outputs (slugs don't collide across the two namespaces
  // because the parser rejects collisions). Includes derived accessors with
  // their canonical target units regardless of species (those don't require
  // any specific property to be defined; the framework figures it out).
  const ioProperties = useMemo(() => {
    const out = {};
    (watchedInputs ?? []).forEach((io, idx) => {
      const slug = inputSlugs[idx];
      const props = typeToPropertiesMap?.[io.species] ?? {};
      out[slug] = { ...props };
      // Energy accessors target the species's conventional energy unit
      // (mmBtu for gas-industry fuels, MJ for J-declared species). Other
      // derived accessors use their static target unit.
      const energyUnit = typeToEnergyUnitMap?.[io.species] || 'kWh';
      DERIVED_ACCESSORS.forEach(name => {
        const unit = ENERGY_DERIVED_ACCESSORS.has(name)
          ? energyUnit
          : DERIVED_ACCESSOR_UNITS[name];
        out[slug][name] = { unit };
      });
    });
    (watchedOutputs ?? []).forEach((io, idx) => {
      const slug = outputSlugs[idx];
      const props = typeToPropertiesMap?.[io.species] ?? {};
      out[slug] = { ...props };
      // Derived accessors omitted for non-anchor IOs (can't be read).
    });
    return out;
  }, [watchedInputs, watchedOutputs, inputSlugs, outputSlugs, typeToPropertiesMap, typeToEnergyUnitMap]);

  const watchedBalance = useWatch({ control, name: 'balance' });

  // Tier-1 structural validators. Synchronous, run on every form change.
  // Anything in `errors` blocks submit; anything in `warnings` is informational.
  const validation = useMemo(
    () => collectValidationIssues({
      label: watchedLabel,
      inputs: watchedInputs,
      outputs: watchedOutputs,
      params: watchedParams,
      balance: watchedBalance,
      tea: watchedTea,
    }),
    [watchedLabel, watchedInputs, watchedOutputs, watchedParams, watchedBalance, watchedTea],
  );

  const annotations = useModuleValidation({
    inputs: watchedInputs,
    outputs: watchedOutputs,
    params: watchedParams,
    balance: watchedBalance,
  });

  const {
    changes: speciesChanges,
    acknowledge: acknowledgeSpeciesChange,
    removeRow: removeSpeciesBaselineRow,
    realign: realignSpeciesBaseline,
    reset: resetSpeciesBaseline,
  } = useSpeciesBaseline({
    inputs: watchedInputs,
    outputs: watchedOutputs,
    inputSlugs,
    outputSlugs,
    balance: watchedBalance,
  });

  const {
    result: testResult,
    running: testRunning,
    run: onRunTest,
    clear: clearTestResult,
    setResult: setTestResult,
  } = useModuleTest({
    getValues,
    inputs: watchedInputs,
    outputs: watchedOutputs,
    params: watchedParams,
    balance: watchedBalance,
  });

  const [status, setStatus] = useState({ submitting: false, success: false });
  const [error, setError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [moduleInfoOpen, setModuleInfoOpen] = useState(true);
  const [inputsOpen, setInputsOpen] = useState(true);
  const [outputsOpen, setOutputsOpen] = useState(true);
  const [paramsOpen, setParamsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('balance'); // 'balance' | 'tea'
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [externalHelpOpen, setExternalHelpOpen] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  const copyExternalImportPrompt = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(EXTERNAL_IMPORT_PROMPT);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (insecure context, etc.) — the
      // textarea is still selectable so the user can fall back to manual copy.
    }
  }, []);

  const handleClose = () => {
    const { pathwayId, projectId, caseId } = params;
    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`, { replace: true });
  };

  const onAddInput = () => {
    inputsArray.append({ label: defaultSpeciesLabel, species: defaultSpecies });
  };

  const onAddOutput = () => {
    const isEmission = EMISSION_SPECIES.has(defaultSpecies);
    outputsArray.append({
      label: defaultSpeciesLabel,
      species: defaultSpecies,
      emissions: isEmission,
      allocatable: !isEmission,
    });
  };

  const onAddParam = () => {
    paramsArray.append({
      label: 'New Parameter',
      type: 'numeric',
      unit: 'scalar',
      default: 0,
    });
  };

  // Switching a param's type wipes the now-irrelevant unit (booleans
  // don't have one) and resets the default to a value that makes sense
  // for the new type.
  const onParamTypeChange = (idx, newType) => {
    if (newType === 'boolean') {
      setValue(`params.${idx}.unit`, 'scalar');
      setValue(`params.${idx}.default`, 'false');
    } else {
      setValue(`params.${idx}.default`, 0);
    }
  };

  // Default the label to the species name (lowercase) when the species
  // dropdown changes, unless the user has typed a custom label. We treat
  // these as "auto-generated, safe to replace": empty, a known species name,
  // a known species display label, or one of the template's seed labels.
  const isAutoLabel = currentLabel => {
    if (!currentLabel) return true;
    if (currentLabel in speciesByName) return true;
    if (Object.values(speciesByName).includes(currentLabel)) return true;
    return TEMPLATE_SEED_LABELS.has(currentLabel);
  };

  const onInputSpeciesChange = (idx, species) => {
    const currentLabel = getValues(`inputs.${idx}.label`);
    if (isAutoLabel(currentLabel)) {
      setValue(`inputs.${idx}.label`, speciesByName[species] ?? species);
    }
  };

  const onOutputSpeciesChange = (idx, species) => {
    const currentLabel = getValues(`outputs.${idx}.label`);
    if (isAutoLabel(currentLabel)) {
      setValue(`outputs.${idx}.label`, speciesByName[species] ?? species);
    }
    const isEmission = EMISSION_SPECIES.has(species);
    setValue(`outputs.${idx}.emissions`, isEmission);
    // emissions and allocatable are mutually exclusive — flipping species
    // to/from an emission default resets allocatable to the natural value.
    setValue(`outputs.${idx}.allocatable`, !isEmission);
  };

  // When the user toggles the Emissions checkbox directly, force allocatable
  // to the opposite. Emissions outputs can't be allocatable.
  const onOutputEmissionsChange = (idx, emissions) => {
    if (emissions) {
      setValue(`outputs.${idx}.allocatable`, false);
    }
  };

  const aiBridge = useAiModuleBridge({
    getValues,
    annotations,
    testResult,
    inputsArray,
    outputsArray,
    paramsArray,
    setValue,
    setTestResult,
    onRowsReplaced: realignSpeciesBaseline,
  });

  // Form starts blank. "Clear" wipes any user edits back to that blank
  // state; "Load example" replaces whatever's there with the canned
  // natural-gas-power-plant template.
  const isFormPristine = useCallback(() => {
    const v = getValues();
    return (
      !v.label?.trim() &&
      !v.description?.trim() &&
      !v.balance?.trim() &&
      (v.inputs ?? []).every(i => !i?.label?.trim()) &&
      (v.outputs ?? []).every(o => !o?.label?.trim()) &&
      (v.params ?? []).length === 0
    );
  }, [getValues]);

  // useForm takes its defaults on the first render, before the species list
  // has loaded, so the blank form's rows start with no species. Reseed it
  // once species arrive, as long as nothing has been entered yet.
  useEffect(() => {
    if (mode === 'create' && defaultSpecies && isFormPristine()) reset(newBlankForm());
  }, [mode, defaultSpecies, isFormPristine, reset, newBlankForm]);

  const onClearForm = useCallback(() => {
    if (!isFormPristine() && !window.confirm('Clear everything and start from a blank form?')) {
      return;
    }
    reset(newBlankForm());
    resetSpeciesBaseline();
    clearTestResult();
  }, [reset, newBlankForm, isFormPristine, clearTestResult, resetSpeciesBaseline]);

  const onLoadExample = useCallback(() => {
    if (!isFormPristine() && !window.confirm('Replace the current form with the example template?')) {
      return;
    }
    reset(exampleDefaults());
    resetSpeciesBaseline();
    clearTestResult();
  }, [reset, isFormPristine, clearTestResult, resetSpeciesBaseline]);

  const onSubmit = async data => {
    setError(null);
    setStatus({ submitting: true, success: false });

    try {
      const { data: created } = await addCustomModel(buildCreatePayload(data));
      await registryActions.refresh();
      setStatus({ submitting: false, success: true });
      // Land the user on the new module in the palette rather than leaving
      // them to find which category it went into.
      registryActions.highlightCustomModule(created?.id);
      handleClose();
    } catch (err) {
      const errData = err?.response?.data;
      setError(errData ? JSON.stringify(errData, null, 2) : err.message || 'Unknown error');
      setStatus({ submitting: false, success: false });
    }
  };

  if (speciesLoading) {
    return (
      <dialog className="modal" open>
        <div className="modal-box p-6">
          <span className="loading loading-spinner"></span> Loading…
        </div>
      </dialog>
    );
  }

  if (mode === 'duplicate' && !customModel?.data) {
    return (
      <dialog className="modal" open>
        <div className="modal-box p-6">
          <h3 className="font-bold text-lg mb-2">Couldn&apos;t open this module</h3>
          <p className="text-sm text-gray-600 mb-3">
            {customModel ? "This module's definition isn't available to you." : 'Failed to load the module.'}
          </p>
          <div className="flex justify-end">
            <button type="button" className="btn btn-sm btn-ghost" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </dialog>
    );
  }

  // Every species dropdown in the form is driven by this data, so a failed
  // load would otherwise present as a builder where nothing can be picked.
  if (speciesError) {
    return (
      <dialog className="modal" open>
        <div className="modal-box p-6">
          <h3 className="font-bold text-lg mb-2">Couldn&apos;t load species types</h3>
          <p className="text-sm text-gray-600 mb-1">
            The equation builder needs the species registry to populate its input and output
            pickers.
          </p>
          <pre className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 overflow-auto mb-3">
            {speciesError}
          </pre>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-sm btn-ghost" onClick={handleClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-sm btn-accent" onClick={retrySpecies}>
              Retry
            </button>
          </div>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className="modal bg-gray-900/30" open>
      <div
        className={`modal-box w-[95vw] h-[90vh] max-h-[90vh] p-0 flex border border-gray-200 shadow-2xl ${
          aiPanelOpen ? 'max-w-[1760px]' : 'max-w-[1400px]'
        }`}
      >
        {/* Header and footer stay put; only the form body between them
            scrolls, so Save and whatever is blocking it are always visible. */}
        <div className="flex-1 min-w-0 flex flex-col">
        <div className="shrink-0 px-6 pt-5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-lg flex items-center gap-2">
            {mode === 'duplicate' ? 'Duplicate Equation Module' : 'Equation Module Builder'}
            <span className="badge badge-sm badge-outline text-gray-500">BETA</span>
          </h3>
          <div className="flex items-center gap-1">
            <a
              href={GUIDE_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-xs btn-ghost gap-1"
              title="How to build a module with equations"
            >
              <BookOpenIcon className="h-3 w-3" />
              Guide
            </a>
            <button type="button" className="btn btn-sm btn-circle btn-ghost" onClick={handleClose}>
              ✕
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Model a process with inputs, outputs, parameters and balance equations.
        </p>

        {/* Ways to get a first draft. The document flow ends in the AI
            assistant, so it needs the agent too. */}
        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
          <span className="text-gray-500">Start from:</span>
          {mode === 'create' && (
            <button
              type="button"
              className="btn btn-xs btn-outline gap-1"
              onClick={onLoadExample}
              title="Populate the form with an example natural-gas power plant module"
            >
              <BeakerIcon className="h-3 w-3" />
              Load example
            </button>
          )}
          {isAgentEnabled && (
            <>
              <button
                type="button"
                className={`btn btn-xs gap-1 ${externalHelpOpen ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setExternalHelpOpen(o => !o)}
                title="Summarize a paper or PDF with Claude or ChatGPT, then paste the result into the AI assistant to set up the module"
              >
                <DocumentTextIcon className="h-3 w-3" />
                Build from a document
              </button>
              <button
                type="button"
                className={`btn btn-xs gap-1 ${aiPanelOpen ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setAiPanelOpen(o => !o)}
                title="Describe the process and let the AI assistant draft the module"
              >
                <SparklesIcon className="h-3 w-3" />
                AI assistant
              </button>
            </>
          )}
        </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 flex flex-col">
        {externalHelpOpen && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-xs">
            <div className="flex items-start justify-between mb-2">
              <div className="font-semibold text-blue-900">
                Build from a PDF or paper using an external LLM
              </div>
              <button
                type="button"
                className="text-blue-700 hover:text-blue-900 text-base leading-none"
                onClick={() => setExternalHelpOpen(false)}
                title="Hide"
              >
                ✕
              </button>
            </div>
            <ol className="list-decimal pl-5 space-y-1 text-blue-900 mb-2">
              <li>Copy the prompt below.</li>
              <li>
                Open <a href="https://claude.ai" target="_blank" rel="noreferrer" className="underline">claude.ai</a>{' '}
                or <a href="https://chat.openai.com" target="_blank" rel="noreferrer" className="underline">ChatGPT</a>,
                upload your document, and paste this prompt.
              </li>
              <li>
                Paste the response into the{' '}
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() => { setAiPanelOpen(true); setExternalHelpOpen(false); }}
                >
                  AI assistant
                </button>{' '}
                — it will set up your module from the structured output.
              </li>
            </ol>
            <div className="relative">
              <pre className="bg-white border border-blue-200 rounded p-2 pr-9 text-[11px] leading-snug text-gray-800 whitespace-pre-wrap max-h-56 overflow-y-auto select-text">
                {EXTERNAL_IMPORT_PROMPT}
              </pre>
              <button
                type="button"
                className="absolute top-1.5 right-1.5 btn btn-xs btn-ghost gap-1"
                onClick={copyExternalImportPrompt}
                title="Copy prompt to clipboard"
              >
                {promptCopied ? (
                  <>
                    <CheckIcon className="h-3 w-3 text-emerald-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="h-3 w-3" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {status.success ? (
          <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
            <p className="text-sm text-green-700">Module created — registry refreshed.</p>
            <button type="button" className="btn btn-sm btn-ghost mt-2" onClick={handleClose}>
              Close
            </button>
          </div>
        ) : null}

          {/* Module info ----------------------------------------------- */}
          <SectionHeader
            title="Module"
            collapsed={!moduleInfoOpen}
            onToggle={() => setModuleInfoOpen(o => !o)}
            preview={[watchedLabel, watchedPermissions].filter(Boolean).join(' · ')}
          />
          {moduleInfoOpen && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium">Label</label>
                  <input
                    className="input input-sm input-bordered w-full"
                    {...register('label', { required: true })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Permissions</label>
                  <select className="select select-sm select-bordered w-full" {...register('permissions')}>
                    {PERMISSION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {watchedPermissions === 'project' && (
                    <p className="text-xs text-gray-500 mt-1">Visible to members of this project.</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs font-medium">Description</label>
                <textarea
                  className="textarea textarea-sm textarea-bordered w-full"
                  rows={2}
                  {...register('description')}
                />
              </div>
            </>
          )}

          {/* Switches everything below it. */}
          {/* Sticky offsets are measured inside the scroll body's pt-4, so
              -top-4 pins the tabs flush with its top edge. */}
          <div role="tablist" className="tabs tabs-bordered mb-3 sticky -top-4 z-20 bg-base-100">
            <button
              type="button"
              role="tab"
              className={`tab ${activeTab === 'balance' ? 'tab-active font-medium' : ''}`}
              onClick={() => setActiveTab('balance')}
            >
              Balance
            </button>
            <button
              type="button"
              role="tab"
              className={`tab ${activeTab === 'tea' ? 'tab-active font-medium' : ''}`}
              onClick={() => setActiveTab('tea')}
            >
              Economics (TEA)
            </button>
          </div>

          {activeTab === 'balance' && (
          <div className="flex-1 flex flex-col">

          {/* Inputs + Outputs side by side --------------------------- */}
          <div className={`grid grid-cols-2 gap-4 ${inputsOpen || outputsOpen ? 'mb-4' : 'mb-1'}`}>
            <div>
              <SectionHeader
                title="Inputs"
                tooltip="The first input is the anchor: the balance is written per unit of its flow, and everything else scales with it."
                onAdd={onAddInput}
                collapsed={!inputsOpen}
                onToggle={() => setInputsOpen(o => !o)}
                preview={inputSlugs.join(', ')}
              />
              {inputsOpen && (
              <table className="table table-xs w-full">
                <thead className="text-xs text-gray-600">
                  <tr>
                    <th className="w-2/5">Species</th>
                    <th>Label</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {inputsArray.fields.map((field, idx) => (
                    <tr key={field.id}>
                      <td>
                        <div className="flex items-center gap-1">
                          <select
                            className="select select-xs select-bordered w-full"
                            {...register(`inputs.${idx}.species`, {
                              onChange: e => onInputSpeciesChange(idx, e.target.value),
                            })}
                          >
                            {typeOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap">
                            {inputUnits[idx]}
                          </span>
                        </div>
                      </td>
                      <td>
                        <input
                          className="input input-xs input-bordered w-full"
                          {...register(`inputs.${idx}.label`)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs px-1"
                          onClick={() => {
                            inputsArray.remove(idx);
                            removeSpeciesBaselineRow('inputs', idx);
                          }}
                          disabled={inputsArray.fields.length === 1}
                        >
                          <TrashIcon className="h-4 w-4 text-gray-500" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>

            <div>
              <SectionHeader
                title="Outputs"
                onAdd={onAddOutput}
                collapsed={!outputsOpen}
                onToggle={() => setOutputsOpen(o => !o)}
                preview={outputSlugs.join(', ')}
              />
              {outputsOpen && (
              <table className="table table-xs w-full">
                <thead className="text-xs text-gray-600">
                  <tr>
                    <th className="w-2/5">Species</th>
                    <th>Label</th>
                    <th className="w-12 text-center">
                      <Tooltip
                        label="Emissions"
                        text="Output is an emission released to the environment, not a product. Will not be allocated and is automatically excluded from TEA reference products."
                      >
                        <span className="cursor-help">Emit</span>
                      </Tooltip>
                    </th>
                    <th className="w-12 text-center">
                      <Tooltip
                        label="Allocatable"
                        text="LCA / TEA impacts are attributed to this product. Uncheck for non-emission byproducts (captured CO₂, water condensate, low-grade heat) that shouldn't share environmental burden with the primary product. Required for at least one output."
                      >
                        <span className="cursor-help">Alloc</span>
                      </Tooltip>
                    </th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {outputsArray.fields.map((field, idx) => {
                    const isEmission = !!watchedOutputs?.[idx]?.emissions;
                    return (
                    <tr key={field.id}>
                      <td>
                        <div className="flex items-center gap-1">
                          <select
                            className="select select-xs select-bordered w-full"
                            {...register(`outputs.${idx}.species`, {
                              onChange: e => onOutputSpeciesChange(idx, e.target.value),
                            })}
                          >
                            {typeOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap">
                            {outputUnits[idx]}
                          </span>
                        </div>
                      </td>
                      <td>
                        <input
                          className="input input-xs input-bordered w-full"
                          {...register(`outputs.${idx}.label`)}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs"
                          {...register(`outputs.${idx}.emissions`, {
                            onChange: e => onOutputEmissionsChange(idx, e.target.checked),
                          })}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs"
                          disabled={isEmission}
                          {...register(`outputs.${idx}.allocatable`)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs px-1"
                          onClick={() => {
                            outputsArray.remove(idx);
                            removeSpeciesBaselineRow('outputs', idx);
                          }}
                          disabled={outputsArray.fields.length === 1}
                        >
                          <TrashIcon className="h-4 w-4 text-gray-500" />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </div>
          </div>

          {/* Params --------------------------------------------------- */}
          <SectionHeader
            title="Parameters"
            onAdd={onAddParam}
            collapsed={!paramsOpen}
            onToggle={() => setParamsOpen(o => !o)}
            preview={paramSlugs.join(', ')}
          />
          {paramsOpen && (
          <table className="table table-xs w-auto mb-4 self-start">
            <thead className="text-xs text-gray-600">
              <tr>
                <th className="w-48">Label</th>
                <th className="w-24">Type</th>
                <th className="w-28">Unit</th>
                <th className="w-24">Default</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {paramsArray.fields.map((field, idx) => {
                const isBoolean = watchedParams?.[idx]?.type === 'boolean';
                return (
                  <tr key={field.id}>
                    <td>
                      <input
                        className="input input-xs input-bordered w-full"
                        {...register(`params.${idx}.label`)}
                      />
                    </td>
                    <td>
                      <select
                        className="select select-xs select-bordered w-full"
                        {...register(`params.${idx}.type`, {
                          onChange: e => onParamTypeChange(idx, e.target.value),
                        })}
                      >
                        {PARAM_TYPES.map(t => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {isBoolean ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <select
                          className="select select-xs select-bordered w-full"
                          {...register(`params.${idx}.unit`)}
                        >
                          {PARAM_UNITS.map(u => (
                            <option key={u.value} value={u.value}>
                              {u.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      {isBoolean ? (
                        <select
                          className="select select-xs select-bordered w-full"
                          {...register(`params.${idx}.default`)}
                        >
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          inputMode="decimal"
                          className="input input-xs input-bordered w-full"
                          {...register(`params.${idx}.default`)}
                        />
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs px-1"
                        onClick={() => paramsArray.remove(idx)}
                      >
                        <TrashIcon className="h-4 w-4 text-gray-500" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}

          {/* Balance -------------------------------------------------- */}
          <div className="flex items-center justify-between border-b border-gray-200 pb-1 mb-2">
            <h4 className="font-medium text-sm">Balance</h4>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                onClick={() => setHelpOpen(o => !o)}
              >
                <ChevronRightIcon
                  className={`h-3 w-3 transition-transform ${helpOpen ? 'rotate-90' : ''}`}
                />
                Syntax reference
              </button>
              <button
                type="button"
                className="btn btn-xs btn-outline"
                onClick={onRunTest}
                disabled={testRunning || validation.hasBlocking}
                title={validation.hasBlocking ? 'Fix the errors below to enable a test run' : ''}
              >
                {testRunning ? 'Running…' : 'Run test'}
              </button>
            </div>
          </div>
          {helpOpen && <HelpSection />}
          <SymbolSummary
            inputSlugs={inputSlugs}
            outputSlugs={outputSlugs}
            paramSlugs={paramSlugs}
            inputUnits={inputUnits}
            outputUnits={outputUnits}
            paramUnits={paramUnits}
          />
          <SpeciesChangeNotice
            changes={speciesChanges}
            speciesLabel={species => speciesByName[species] ?? species}
            rowLabel={({ kind, idx }) =>
              (kind === 'inputs' ? watchedInputs : watchedOutputs)?.[idx]?.label || `#${idx + 1}`
            }
            onAcknowledge={acknowledgeSpeciesChange}
          />
          <BalanceEditor
            className="flex-1 mb-3"
            control={control}
            register={register}
            setValue={setValue}
            inputSlugs={inputSlugs}
            outputSlugs={outputSlugs}
            paramSlugs={paramSlugs}
            inputUnits={inputUnits}
            outputUnits={outputUnits}
            paramUnits={paramUnits}
            ioProperties={ioProperties}
            annotations={annotations.byLine}
          />

          {testResult && <TestResultsPanel result={testResult} onClose={clearTestResult} />}

          {annotations.specErrors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800 mb-3">
              {annotations.specErrors.map((e, idx) => (
                <div key={idx}>
                  {e.field ? <strong>{e.field}: </strong> : null}
                  {e.message}
                </div>
              ))}
            </div>
          )}

          </div>
          )}

          {/* TEA tab ----------------------------------------------------- */}
          {activeTab === 'tea' && (
            <TEASection
              register={register}
              setValue={setValue}
              watchedTea={watchedTea}
              watchedOutputs={watchedOutputs}
              outputSlugs={outputSlugs}
              outputUnits={outputUnits}
            />
          )}

        </div>

          <div className="shrink-0 px-6 pb-4 pt-3 bg-gray-50 border-t border-gray-200">
          <div className="max-h-[30vh] overflow-y-auto">
          {error && (
            <pre className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 overflow-auto mb-3">
              {error}
            </pre>
          )}

          {validation.hasBlocking && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 mb-3">
              <div className="font-medium mb-1">
                Fix the following before saving:
              </div>
              <ul className="list-disc pl-4 space-y-0.5">
                {validation.issues
                  .filter(i => i.severity === 'error')
                  .map((i, idx) => (
                    <li key={`${i.path}-${idx}`}>{i.message}</li>
                  ))}
              </ul>
            </div>
          )}

          {!validation.hasBlocking &&
            validation.issues.some(i => i.severity === 'warning') && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 mb-3">
                <div className="font-medium mb-1">
                  Warnings (won&apos;t block saving):
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {validation.issues
                    .filter(i => i.severity === 'warning')
                    .map((i, idx) => (
                      <li key={`${i.path}-${idx}`}>{i.message}</li>
                    ))}
                </ul>
              </div>
            )}

          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {mode === 'create' && (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost text-gray-500"
                  onClick={onClearForm}
                  title="Reset the form to a blank state"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                Saves to the <strong>Custom</strong> category in Modules
              </span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={handleClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-sm btn-accent"
                disabled={status.submitting || validation.hasBlocking}
              >
                {status.submitting ? 'Saving…' : 'Save module'}
              </button>
            </div>
          </div>
          </div>
        </form>
        </div>

        {isAgentEnabled && aiPanelOpen && (
          <div className="w-[360px] flex-shrink-0">
            <AIChatPanel
              getFormSnapshot={aiBridge.getFormSnapshot}
              getAnnotations={aiBridge.getAnnotations}
              getLastTestResult={aiBridge.getLastTestResult}
              onModuleUpdate={aiBridge.onModuleUpdate}
              onTestResult={aiBridge.onTestResult}
            />
          </div>
        )}
      </div>
    </dialog>
  );
};

CreateEquationModelModal.propTypes = {
  mode: PropTypes.oneOf(['create', 'duplicate']),
};

export default CreateEquationModelModal;
